<script setup>
import { ref, computed, watch } from 'vue'
import AssessmentQuestionCard from '../../AssessmentQuestionCard.vue'
import { getOptionLabels, isOptionVisible, parseConditionalOptions } from '../../../utils/questionsAndAnswersHelpers'

const props = defineProps({
  actionItem: { type: Object, required: true },
  answer: { type: [String, Array, Object, Number], default: null },
  notes: { type: String, default: '' },
  // questionId -> answer for the plan year; drives conditional options (e.g. ACA "N/A").
  answersMap: { type: Object, default: () => ({}) },
  isSaving: { type: Boolean, default: false }
})

const emit = defineEmits(['back', 'complete', 'update:answer', 'update:notes'])

const showNotesAccordion = ref(false)

const initialAnswer = props.answer

const localAnswer = ref(props.answer)

watch(() => props.answer, (newValue) => {
  localAnswer.value = newValue
})

// Get question metadata from action item
// Question properties are now at root level of actionItem
// Create a reactive copy with the current answer merged in
const currentQuestion = computed(() => {
  if (!props.actionItem) return null
    // Convert answer to correct type based on question type
  // Radio (type 0), Select (type 2), ERISA (type 4) use numeric indexes
  // Checkbox (type 1) uses array of numbers
  let processedAnswer = localAnswer.value
  
  if (localAnswer.value !== null && localAnswer.value !== undefined) {
    const questionType = props.actionItem.type
    
    if (questionType === 0 || questionType === 2 || questionType === 4) {
      // Radio, Select, ERISA: convert string to number
      processedAnswer = typeof localAnswer.value === 'string' ? parseInt(localAnswer.value, 10) : localAnswer.value
    } else if (questionType === 1) {
      // Checkbox: ensure it's an array of numbers
      if (typeof localAnswer.value === 'string') {
        try {
          const parsed = JSON.parse(localAnswer.value)
          processedAnswer = Array.isArray(parsed) ? parsed.map(v => parseInt(v, 10)) : [parseInt(localAnswer.value, 10)]
        } catch {
          processedAnswer = [parseInt(localAnswer.value, 10)]
        }
      } else if (Array.isArray(localAnswer.value)) {
        processedAnswer = localAnswer.value.map(v => typeof v === 'string' ? parseInt(v, 10) : v)
      }
    }
  }

  // Exclude wrong answers and conditional options whose condition is not met.
  const wrongAnswerIndexes = props.actionItem.wrongAnswer?.values || []
  const hasConditionalOptions = Object.keys(parseConditionalOptions(props.actionItem)).length > 0
  if (!wrongAnswerIndexes.length && !hasConditionalOptions) {
    // No filtering needed, use original options
    return {
      ...props.actionItem,
      answer: processedAnswer
    }
  }

  const getAnswer = (questionId) => props.answersMap?.[questionId]
  const filteredOptions = []
  const optionIndexMap = []
  getOptionLabels(props.actionItem).forEach((option, index) => {
    if (!wrongAnswerIndexes.includes(index) && isOptionVisible(props.actionItem, index, getAnswer)) {
      filteredOptions.push(option)
      optionIndexMap.push(index)
    }
  })

  if (processedAnswer !== null && processedAnswer !== undefined) {
    const questionType = props.actionItem.type

    if (questionType === 0 || questionType === 4) {
      if (typeof processedAnswer === 'number') {
        const filteredIndex = optionIndexMap.indexOf(processedAnswer)
        processedAnswer = filteredIndex >= 0 ? filteredIndex : null
      }
    } else if (questionType === 1) {
      if (Array.isArray(processedAnswer)) {
        processedAnswer = processedAnswer
          .map((v) => optionIndexMap.indexOf(v))
          .filter((v) => v >= 0)
      } else if (typeof processedAnswer === 'number') {
        const filteredIndex = optionIndexMap.indexOf(processedAnswer)
        processedAnswer = filteredIndex >= 0 ? [filteredIndex] : []
      }
    }
  }
  
  // AssessmentQuestionCard expects question.answer to be set
  // Create merged question with FILTERED options (just the correct ones)
  return {
    ...props.actionItem,
    options: {
      ...props.actionItem.options,
      values: filteredOptions  // ← Use filtered options instead of all options
    },
    answer: processedAnswer,
    optionIndexMap
  }
})

// Get section title from question metadata
const questionSection = computed(() => {
  return currentQuestion.value?.section.subtitle
})

// Check if current answer matches at least one filtered answer
const hasValidFilteredAnswer = computed(() => {
  if (!currentQuestion.value || !Array.isArray(currentQuestion.value.optionIndexMap)) {
    return true // No filtering means any answer is valid
  }

  const selected = localAnswer.value
  const correctOriginalIndexes = Array.isArray(currentQuestion.value.optionIndexMap)
    ? currentQuestion.value.optionIndexMap
    : []

  if (selected === null || selected === undefined) return false

  const questionType = props.actionItem.type

  if (questionType === 1) {
    if (!Array.isArray(selected)) return false
    return selected.some((v) => correctOriginalIndexes.includes(v))
  }

  if (questionType === 0 || questionType === 4) {
    if (typeof selected !== 'number') return false
    return correctOriginalIndexes.includes(selected)
  }

  return true
})

function handleBack() {
  emit('back')
}

function handleAnswerChange() {
  // AssessmentQuestionCard mutates question.answer directly
  // Read the updated value from currentQuestion
  const question = currentQuestion.value
  if (!question) return

  let nextAnswer = question.answer

  if (Array.isArray(question.optionIndexMap)) {
    const questionType = props.actionItem.type

    if ((questionType === 0 || questionType === 4) && typeof nextAnswer === 'number') {
      nextAnswer = question.optionIndexMap[nextAnswer]
    } else if (questionType === 1) {
      if (Array.isArray(nextAnswer)) {
        nextAnswer = nextAnswer
          .map((v) => question.optionIndexMap[v])
          .filter((v) => v !== undefined)
      } else if (typeof nextAnswer === 'number') {
        const mapped = question.optionIndexMap[nextAnswer]
        nextAnswer = mapped !== undefined ? [mapped] : []
      }
    }
  }

  localAnswer.value = nextAnswer
  emit('update:answer', nextAnswer)
}

function handleNotesChange(event) {
  emit('update:notes', event.target.value)
}

function handleConfirmComplete() {
  if (props.isSaving || !currentQuestion.value) return
  emit('complete')
}
</script>

<template>
  <div>
    <button id="backtoActionItemsButton" 
        type="button" 
        class="btn btn-link p-0 mb-3 text-14 text-primary text-decoration-none" 
        @click="handleBack">
      <i class="bi bi-chevron-left me-1"></i>
      <strong>Back to Action Items</strong>
    </button>

    <div v-if="currentQuestion">
      <div class="mb-3">
        <p id="questionSectionTitle" class="fw-bold header-22 mt-2 mb-1">{{ questionSection }}</p>
        <p id="questionSectionDescription" class="text-16 text-muted mb-0">We need a few more details about 
          this requirement to complete it. After this step, you’ll see the reduced risk. 
        </p>
      </div>
      <div class="question-container mb-3">
        <AssessmentQuestionCard
          :question="currentQuestion"
          :question-index="0"
          :show-question-number="false"
          :original-answer="initialAnswer"
          :original-options="props.actionItem.options?.values || []"
          :warning="props.actionItem.warning"
          :is-saving="props.isSaving"
          @answer-changed="handleAnswerChange"
        />
      </div>

      <div class="accordion mb-4" id="notesAccordion">
        <div class="accordion-item border-0">
          <h2 class="accordion-header" id="notesHeading">
            <button
              id="addNotesButton"
              class="accordion-button text-16 bg-white border-0 shadow-none px-0"
              :class="{ collapsed: !showNotesAccordion }"
              type="button"
              @click="showNotesAccordion = !showNotesAccordion"
              :aria-expanded="showNotesAccordion"
              aria-controls="notesCollapse"
            >
              <strong class="text-primary">Add Notes (optional)</strong>
            </button>
          </h2>
          <div
            id="notesCollapse"
            class="accordion-collapse collapse"
            :class="{ show: showNotesAccordion }"
            aria-labelledby="notesHeading"
          >
            <div class="accordion-body px-0 pt-3">
              <label class="form-label text-16 fw-bold mb-2">Notes</label>
              <textarea
                class="form-control text-16"
                rows="5"
                :value="notes"
                @input="handleNotesChange"
                placeholder="Add notes here..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between button-container">
        <button
          id="cancelUpdateButton"
          type="button"
          class="btn btn-outline-primary text-16"
          @click="handleBack"
          :disabled="props.isSaving"
        >
          Cancel Update
        </button>
        <button
          id="confirmCompleteButton"
          type="button"
          class="btn btn-primary text-16"
           @click="handleConfirmComplete"
          :disabled="props.isSaving || !hasValidFilteredAnswer"
        >
          {{ props.isSaving ? 'Saving...' : 'Confirm Complete' }}
        </button>
      </div>
    </div>

    <div v-else class="text-center py-4 text-muted">
      <p>Question not found. Please try again.</p>
      <button type="button" class="btn btn-link" @click="handleBack">Back to Action Items</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.question-container {
  padding-top: 1rem;
  border-radius: 0.375rem;
}

.button-container {
  background-color: var(--bs-light);
  padding: 16px;
  position: fixed;
  bottom: 0;
  right: 0;
  width: 510px;
}
</style>
